import { FC, useState } from 'react';
import { TextInput, RadioButtonGroup, RadioButton, Layer } from '@carbon/react';
import { SidePanel } from '@carbon/ibm-products';
import { useAtom, useSetAtom } from 'jotai';
import { EntityForm } from 'components/entity-form';
import { SidePanelResizeHandle } from 'components/resizable-side-panel';
import {
  addNotificationsAtom,
  NotificationGroup,
} from 'components/atomised-notifications';
import { DocumentResponse } from 'types/api-responses';
import type { EntitySearchResult } from 'pages/entities/list/types';
import { addPartyAtom, addPartyUrl } from './atoms/structured-parties';
import { EntityTablePicker } from './entity-table-picker';
import { useExclusiveDrawer } from './exclusive-drawer-hooks';
import { actionErrorMessage } from 'utils/api-errors';
import 'components/inline-edit';
import style from './add-party-panel.module.scss';

type Mode = 'select' | 'create';

interface Props {
  open: boolean;
  document: DocumentResponse;
  onClose: () => void;
  onAdded: () => void;
}

export const AddPartyPanel: FC<Props> = ({
  open,
  document,
  onClose,
  onAdded,
}) => {
  const [role, setRole] = useState('');
  const [mode, setMode] = useState<Mode>('select');
  const [selectedEntity, setSelectedEntity] =
    useState<EntitySearchResult | null>(null);
  const [addState, triggerAdd] = useAtom(addPartyAtom);
  const pending = addState.loading;
  const addNotifications = useSetAtom(addNotificationsAtom);
  const pageContentSelector = useExclusiveDrawer('add-party', open, onClose);

  const notifyError = (message: string) =>
    addNotifications(NotificationGroup.StructuredParties, [
      {
        id: `party-add-error-${Date.now()}`,
        title: 'Could not add party',
        message,
        kind: 'error',
        autoHideMs: 6000,
      },
    ]);

  const submit = (entity: { id: number }) => {
    if (!role.trim()) return;
    triggerAdd({
      url: addPartyUrl(document.id),
      type: 'POST',
      data: { suggested_entity_id: entity.id, role: role.trim() },
      onSuccess: (data) => {
        addNotifications(NotificationGroup.StructuredParties, [
          {
            id: `party-added-${data.entityId}`,
            title: 'Party added',
            message: 'Party added successfully',
            kind: 'success',
            autoHideMs: 5000,
          },
        ]);
        onAdded();
      },
      onError: (err) =>
        notifyError(actionErrorMessage(err, 'Could not add party')),
    });
  };

  const isSearch = mode === 'select';
  const primaryDisabled = !role.trim() || !selectedEntity || pending;

  const actions = isSearch
    ? [
        {
          kind: 'secondary' as const,
          label: 'Cancel',
          onClick: onClose,
        },
        {
          kind: 'primary' as const,
          label: 'Add Party',
          disabled: primaryDisabled,
          onClick: () => {
            if (selectedEntity) submit(selectedEntity);
          },
        },
      ]
    : [
        {
          kind: 'secondary' as const,
          label: 'Cancel',
          onClick: onClose,
        },
      ];

  return (
    <>
      <SidePanelResizeHandle
        open={open}
        panelSelector=".add-party-sidepanel"
        pageContentSelector={pageContentSelector}
        defaultWidthPx={640}
      />
      <SidePanel
        open={open}
        onRequestClose={onClose}
        title="Add Party"
        subtitle="Select a role and entity to add a new party to this document."
        className={`add-party-sidepanel ${style.sidePanel}`}
        slideIn
        selectorPageContent={pageContentSelector}
        placement="right"
        size="lg"
        actions={actions}
      >
        <div className={style.body}>
          <Layer>
            <TextInput
              id="add-party-role"
              labelText="Role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="e.g. Lessor, Lessee, Grantor"
              disabled={pending}
            />
          </Layer>

          <RadioButtonGroup
            name="add-party-mode"
            legendText=""
            orientation="horizontal"
            valueSelected={mode}
            onChange={(value) => {
              setMode(value as Mode);
              setSelectedEntity(null);
            }}
            className={style.pathRadio}
          >
            <RadioButton
              id="add-party-mode-select"
              labelText="Select from Entities"
              value="select"
            />
            <RadioButton
              id="add-party-mode-create"
              labelText="Create New Entity"
              value="create"
            />
          </RadioButtonGroup>

          {isSearch ? (
            <EntityTablePicker
              selectedEntity={selectedEntity}
              onSelect={setSelectedEntity}
            />
          ) : (
            <div className={style.createWrap}>
              <EntityForm
                entity={undefined}
                getEntity={undefined}
                loading={false}
                onSuccess={(entity) => submit(entity)}
                showCancel={false}
              />
            </div>
          )}
        </div>
      </SidePanel>
    </>
  );
};
